import request from '../request';
import { wondeAPI, wondeLegacyAPI } from './urls';
import { WondeSyncScope } from '../../utils/wondeConnectionStatus';

/**
 * Wonde connection & sync API client (redesign).
 *
 * Talks to the consolidated, SchoolId-keyed `api/wonde` controller. Every call
 * resolves to the API envelope `{ success, data, message }`; callers read
 * `data` (a connection/status DTO or a candidate list) off the result.
 */

/** Find a school in Wonde by URN, or — for no-URN/NI schools — by postcode / establishment / LA code. */
const search = ({ schoolId, urn, postcode, establishmentNumber, laCode }) =>
  request({
    url: wondeAPI.search,
    method: 'POST',
    data: {
      SchoolId: schoolId,
      Urn: urn || null,
      Postcode: postcode || null,
      EstablishmentNumber: establishmentNumber || null,
      LaCode: laCode || null,
    },
  })
    .then((response) => response)
    .catch((error) => error);

/** Request a Wonde connection for a chosen candidate (transactional → PendingApproval). */
const connect = ({ schoolId, wondeSchoolId, urn, staffSync = false, studentParentSync = true }) =>
  request({
    url: wondeAPI.connect,
    method: 'POST',
    data: {
      SchoolId: schoolId,
      WondeSchoolId: wondeSchoolId,
      StaffSync: staffSync,
      StudentParentSync: studentParentSync,
      // Forward the chosen candidate's URN so the stored URN can't drift from the linked Wonde school.
      ...(urn ? { Urn: urn } : {}),
    },
  })
    .then((response) => response)
    .catch((error) => error);

/** Manually link a known Wonde school id (fallback when search finds no match). */
const link = ({ schoolId, wondeSchoolId, staffSync = false, studentParentSync = true }) =>
  request({
    url: wondeAPI.link,
    method: 'POST',
    data: {
      SchoolId: schoolId,
      WondeSchoolId: wondeSchoolId,
      StaffSync: staffSync,
      StudentParentSync: studentParentSync,
    },
  })
    .then((response) => response)
    .catch((error) => error);

/** Current connection view for a single school. */
const getConnection = (schoolId) =>
  request({
    url: wondeAPI.getConnection(schoolId),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);

/** Connection views for every school in a MAT/trust. */
const getGroupConnections = (matId) =>
  request({
    url: wondeAPI.getGroupConnections(matId),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);

/** Save sync settings only — does NOT trigger a sync run. */
const saveSyncSettings = (schoolId, { staffSync, studentParentSync }) =>
  request({
    url: wondeAPI.saveSyncSettings(schoolId),
    method: 'PUT',
    data: {
      StaffSync: staffSync,
      StudentParentSync: studentParentSync,
    },
  })
    .then((response) => response)
    .catch((error) => error);

/** Trigger a sync run now — does NOT change saved settings. */
const runSync = (schoolId, scope = WondeSyncScope.All) =>
  request({
    url: wondeAPI.runSync(schoolId, scope),
    method: 'POST',
  })
    .then((response) => response)
    .catch((error) => error);

/** Live sync state for a school (settings + last-synced + last error + staff run state). */
const getSyncStatus = (schoolId) =>
  request({
    url: wondeAPI.getSyncStatus(schoolId),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);

/** Correct the stored URN for a school (does not change the Wonde link). */
const updateUrn = (schoolId, urn) =>
  request({
    url: wondeAPI.updateUrn(schoolId),
    method: 'PUT',
    data: { Urn: urn },
  })
    .then((response) => response)
    .catch((error) => error);

/**
 * Clear a school's Wonde connection. The backend is state-aware: a live request/grant
 * (approved, or pending with a real request) is withdrawn on Wonde first, then local
 * state is reset; an orphaned link is cleared locally only.
 */
const clearConnection = (schoolId) =>
  request({
    url: wondeAPI.clearConnection(schoolId),
    method: 'DELETE',
  })
    .then((response) => response)
    .catch((error) => error);

/**
 * Request access for a chosen set of schools in a MAT/trust, each with its own sync options.
 * `schools` is an array of { schoolId, staffSync, studentParentSync }. The backend resolves each
 * school by URN, re-checks eligibility, skips no-URN / already-connected, and returns a per-school summary.
 */
const bulkRequest = (matId, schools = []) =>
  request({
    url: wondeAPI.bulkRequest(matId),
    method: 'POST',
    data: {
      Schools: (schools || []).map((s) => ({
        SchoolId: s.schoolId,
        StaffSync: Boolean(s.staffSync),
        StudentParentSync: Boolean(s.studentParentSync),
      })),
    },
  })
    .then((response) => response)
    .catch((error) => error);

/**
 * Save sync settings for a chosen set of already-connected schools in a MAT/trust in one call.
 * `schools` is an array of { schoolId, staffSync, studentParentSync }. The backend re-checks each
 * school is approved, applies the flags, skips non-connected rows, and returns a per-school summary.
 */
const bulkSaveSyncSettings = (matId, schools = []) =>
  request({
    url: wondeAPI.bulkSaveSyncSettings(matId),
    method: 'PUT',
    data: {
      Schools: (schools || []).map((s) => ({
        SchoolId: s.schoolId,
        StaffSync: Boolean(s.staffSync),
        StudentParentSync: Boolean(s.studentParentSync),
      })),
    },
  })
    .then((response) => response)
    .catch((error) => error);

// --- Legacy URN-keyed WondeRequest calls (headteacher account screen) ---

const getWondeSchoolRequestStatus = (wondeSchoolUrn) =>
  request({
    url: wondeLegacyAPI.getWondeSchoolRequestStatus(wondeSchoolUrn),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);

const updateWondeRequestStatus = (schoolUrnNo) =>
  request({
    url: wondeLegacyAPI.updateWondeRequestStatus(schoolUrnNo),
    method: 'PUT',
  })
    .then((response) => response)
    .catch((error) => error);

const updateWondeSyncedStatus = (schoolUrnNo) =>
  request({
    url: wondeLegacyAPI.updateWondeSyncedStatus(schoolUrnNo),
    method: 'PUT',
  })
    .then((response) => response)
    .catch((error) => error);

const wondeService = {
  search,
  connect,
  link,
  getConnection,
  getGroupConnections,
  saveSyncSettings,
  runSync,
  getSyncStatus,
  updateUrn,
  clearConnection,
  bulkRequest,
  bulkSaveSyncSettings,
  // legacy
  getWondeSchoolRequestStatus,
  updateWondeRequestStatus,
  updateWondeSyncedStatus,
};

export default wondeService;
